import { jsPDF } from 'jspdf';
import {
  buildAgreementDownloadFilename,
  isAgreementLanguageAvailable,
  resolveAgreementDocument,
} from './agreementDocuments';
import { formatAgreementDateLabel, getAgreementBodyContent } from './agreementContent';

function wrapText(doc, text, maxWidth) {
  return doc.splitTextToSize(String(text || ''), maxWidth);
}

/**
 * Build a PDF Blob from approved structured agreement content (same text shown in UI).
 */
export async function generateAgreementPdfBlob(agreementKey, languageCode) {
  const resolved = resolveAgreementDocument(agreementKey, languageCode);
  if (resolved.status !== 'available') {
    throw new Error(resolved.message || 'Agreement translation is not available for download.');
  }

  // Prefer a static approved PDF when configured
  if (resolved.staticPdfUrl) {
    const res = await fetch(resolved.staticPdfUrl);
    if (!res.ok) {
      throw new Error('Failed to download the agreement document. Please try again.');
    }
    return {
      blob: await res.blob(),
      filename: resolved.downloadFilename,
    };
  }

  const content = getAgreementBodyContent(agreementKey, resolved.displayLanguage);
  if (!content) {
    throw new Error('Approved agreement content is not available for this language.');
  }

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 48;
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed = 18) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  doc.setFont('times', 'bold');
  doc.setFontSize(14);
  wrapText(doc, content.heading, maxWidth).forEach((line) => {
    ensureSpace(18);
    doc.text(line, pageWidth / 2, y, { align: 'center' });
    y += 18;
  });

  doc.setFont('times', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(100);
  ensureSpace(14);
  doc.text(content.companyLine, pageWidth / 2, y, { align: 'center' });
  y += 22;
  doc.setTextColor(0);

  const dateLabel = formatAgreementDateLabel();
  const meta = typeof content.metaRows === 'function' ? content.metaRows(dateLabel) : content.metaRows || [];
  meta.forEach(([k, v]) => {
    ensureSpace(16);
    doc.setFont('times', 'bold');
    doc.text(`${k}:`, margin, y);
    doc.setFont('times', 'normal');
    const valueLines = wrapText(doc, v, maxWidth - 140);
    doc.text(valueLines, margin + 140, y);
    y += Math.max(16, valueLines.length * 14);
  });
  y += 10;

  (content.sections || []).forEach((section) => {
    ensureSpace(22);
    doc.setFont('times', 'bold');
    doc.setFontSize(11);
    wrapText(doc, section.title, maxWidth).forEach((line) => {
      ensureSpace(14);
      doc.text(line, margin, y);
      y += 14;
    });
    doc.setFont('times', 'normal');
    doc.setFontSize(10);
    if (section.body) {
      wrapText(doc, section.body, maxWidth).forEach((line) => {
        ensureSpace(13);
        doc.text(line, margin, y);
        y += 13;
      });
      y += 4;
    }
    (section.bullets || []).forEach((bullet) => {
      const lines = wrapText(doc, `• ${bullet}`, maxWidth - 12);
      lines.forEach((line) => {
        ensureSpace(13);
        doc.text(line, margin + 12, y);
        y += 13;
      });
      y += 2;
    });
    y += 8;
  });

  if (content.footer) {
    y += 6;
    ensureSpace(20);
    doc.setDrawColor(200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 14;
    doc.setFontSize(9);
    doc.setTextColor(80);
    wrapText(doc, content.footer, maxWidth).forEach((line) => {
      ensureSpace(12);
      doc.text(line, margin, y);
      y += 12;
    });
    doc.setTextColor(0);
  }

  return {
    blob: doc.output('blob'),
    filename: resolved.downloadFilename || buildAgreementDownloadFilename(agreementKey, languageCode),
  };
}

export async function downloadAgreementPdf(agreementKey, languageCode) {
  if (!isAgreementLanguageAvailable(agreementKey, languageCode)) {
    const resolved = resolveAgreementDocument(agreementKey, languageCode);
    throw new Error(resolved.message || 'Download is unavailable for the selected language.');
  }
  const { blob, filename } = await generateAgreementPdfBlob(agreementKey, languageCode);
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
  return { filename };
}
